/**
 * ImageViewer — full-screen photo viewer with pinch-to-zoom, pan and
 * double-tap zoom. Body-level singleton so any surface can open a picture
 * with one call: `ImageViewer.open(url)`.
 *
 * Used by: PlayerCard avatar, friend-request notification avatar. (Phone Dice
 * players love checking each other's profile pics — this brings that across.)
 *
 * Touch model (Pointer Events, works for touch + mouse):
 *   - 1 finger  → pan (only when zoomed in)
 *   - 2 fingers → pinch zoom around the gesture midpoint (+ two-finger pan)
 *   - double-tap → toggle 1× / 2.5× centred on the tap
 *   - wheel     → zoom around the cursor (desktop)
 *   - tap backdrop / close button / Esc → dismiss
 */

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 24;
const STYLE_ID = 'sdl-image-viewer-styles';

class ImageViewerImpl {
  private host: HTMLElement | null = null;
  private img!: HTMLImageElement;
  private stage!: HTMLElement;

  // transform state (transform-origin: 0 0 on the image box)
  private scale = 1;
  private tx = 0;
  private ty = 0;

  private pointers = new Map<number, { x: number; y: number }>();
  private prevDist = 0;
  private prevMid = { x: 0, y: 0 };
  private panStart = { x: 0, y: 0 };
  private lastTapAt = 0;
  private lastTapPos = { x: 0, y: 0 };
  private moved = false;

  open(url: string | undefined | null): void {
    const src = (url ?? '').trim();
    if (!src) return; // nothing to show (initials-only avatars)
    this.ensureDom();
    this.reset();
    this.img.src = src;
    this.host!.classList.add('visible');
  }

  close(): void {
    if (!this.host) return;
    this.host.classList.remove('visible');
    // Drop the src after the fade so we don't hold a decoded bitmap around.
    setTimeout(() => {
      if (this.host && !this.host.classList.contains('visible')) this.img.src = '';
    }, 220);
  }

  // ---------- DOM ----------

  private ensureDom(): void {
    if (this.host) return;
    ImageViewerImpl.injectStyles();

    this.host = document.createElement('div');
    this.host.className = 'sdl-iv-host';
    this.host.innerHTML = `
      <div class="sdl-iv-backdrop"></div>
      <div class="sdl-iv-stage">
        <img class="sdl-iv-img" alt="" draggable="false">
      </div>
      <button class="sdl-iv-close" aria-label="Close">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `;
    document.body.appendChild(this.host);

    this.stage = this.host.querySelector('.sdl-iv-stage') as HTMLElement;
    this.img = this.host.querySelector('.sdl-iv-img') as HTMLImageElement;

    this.host.querySelector('.sdl-iv-close')!.addEventListener('click', () => this.close());
    this.host.querySelector('.sdl-iv-backdrop')!.addEventListener('click', () => this.close());

    // Pointer / gesture handling on the stage.
    this.stage.addEventListener('pointerdown', this.onPointerDown);
    this.stage.addEventListener('pointermove', this.onPointerMove);
    this.stage.addEventListener('pointerup', this.onPointerUp);
    this.stage.addEventListener('pointercancel', this.onPointerUp);
    this.stage.addEventListener('wheel', this.onWheel, { passive: false });
    document.addEventListener('keydown', this.onKeyDown);
  }

  private reset(): void {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.pointers.clear();
    this.applyTransform();
  }

  private applyTransform(): void {
    if (this.scale <= MIN_SCALE + 0.001) {
      this.scale = MIN_SCALE;
      this.tx = 0;
      this.ty = 0;
    }
    this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
    this.stage.classList.toggle('zoomed', this.scale > 1);
  }

  private clampScale(s: number): number {
    return Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));
  }

  /** Zoom around a focal point (in stage-box pixels) keeping it fixed. */
  private zoomTo(newScale: number, focalX: number, focalY: number): void {
    const s = this.clampScale(newScale);
    const ratio = s / this.scale;
    this.tx = focalX - (focalX - this.tx) * ratio;
    this.ty = focalY - (focalY - this.ty) * ratio;
    this.scale = s;
    this.applyTransform();
  }

  // ---------- Pointer handlers (bound) ----------

  private localPoint(e: PointerEvent): { x: number; y: number } {
    const r = this.stage.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private onPointerDown = (e: PointerEvent): void => {
    this.stage.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.moved = false;

    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.prevDist = Math.hypot(a.x - b.x, a.y - b.y);
      this.prevMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    } else if (this.pointers.size === 1) {
      const p = this.localPoint(e);
      this.panStart = { x: p.x - this.tx, y: p.y - this.ty };
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const r = this.stage.getBoundingClientRect();
      const focalX = mid.x - r.left;
      const focalY = mid.y - r.top;
      if (this.prevDist > 0) {
        this.zoomTo(this.scale * (dist / this.prevDist), focalX, focalY);
        // two-finger pan: follow the midpoint drift
        this.tx += mid.x - this.prevMid.x;
        this.ty += mid.y - this.prevMid.y;
        this.applyTransform();
      }
      this.prevDist = dist;
      this.prevMid = mid;
      this.moved = true;
    } else if (this.pointers.size === 1 && this.scale > 1) {
      const p = this.localPoint(e);
      this.tx = p.x - this.panStart.x;
      this.ty = p.y - this.panStart.y;
      this.applyTransform();
      this.moved = true;
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.prevDist = 0;
    if (this.pointers.size === 1) {
      // Re-anchor the remaining finger for continued panning.
      const remaining = [...this.pointers.values()][0];
      const r = this.stage.getBoundingClientRect();
      this.panStart = { x: remaining.x - r.left - this.tx, y: remaining.y - r.top - this.ty };
    }

    if (this.pointers.size === 0 && !this.moved) {
      // Tap — detect double-tap to toggle zoom.
      const now = Date.now();
      const near =
        Math.hypot(e.clientX - this.lastTapPos.x, e.clientY - this.lastTapPos.y) < DOUBLE_TAP_SLOP;
      if (now - this.lastTapAt < DOUBLE_TAP_MS && near) {
        const p = this.localPoint(e);
        this.zoomTo(this.scale > 1 ? MIN_SCALE : 2.5, p.x, p.y);
        this.lastTapAt = 0;
      } else {
        this.lastTapAt = now;
        this.lastTapPos = { x: e.clientX, y: e.clientY };
      }
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const r = this.stage.getBoundingClientRect();
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.zoomTo(this.scale * factor, e.clientX - r.left, e.clientY - r.top);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.host?.classList.contains('visible')) this.close();
  };

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-iv-host {
        position: fixed;
        inset: 0;
        z-index: 6000;
        display: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        touch-action: none;
      }
      .sdl-iv-host.visible { display: block; opacity: 1; }
      .sdl-iv-backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.92);
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
      }
      .sdl-iv-stage {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        touch-action: none;
        cursor: zoom-in;
      }
      .sdl-iv-stage.zoomed { cursor: grab; }
      .sdl-iv-img {
        max-width: 100%;
        max-height: 100%;
        width: 100%;
        height: 100%;
        object-fit: contain;
        transform-origin: 0 0;
        will-change: transform;
        user-select: none;
        -webkit-user-select: none;
        -webkit-user-drag: none;
      }
      .sdl-iv-close {
        position: absolute;
        top: calc(env(safe-area-inset-top) + 12px);
        right: 14px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 50%;
        color: #fff;
        cursor: pointer;
        transition: background 0.15s, transform 0.1s;
      }
      .sdl-iv-close:active { transform: scale(0.92); background: rgba(255, 255, 255, 0.18); }
    `;
    document.head.appendChild(style);
  }
}

/** Singleton — open a photo from anywhere via `ImageViewer.open(url)`. */
export const ImageViewer = new ImageViewerImpl();
